import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import {
  complaintReasonOptions, HEALTH_DATA_CATEGORIES, HEALTH_DATA_COMPLAINT_REASON,
  HEALTH_DATA_NOTE, ORGANIZATION_RELATION_OPTIONS, reportCategoryOptions, TYPE_LABELS,
  WORKER_TYPE_OPTIONS,
} from '../../../core/occurrence/occurrence.labels';
import {
  ComplaintReason, IncidentNature, OccurrenceType, OrganizationRelation, ReportCategory, ReporterNature, ReporterNatureDetail, WorkerType,
} from '../../../core/occurrence/occurrence.models';
import {
  NATURE_CHOICES, PUBLIC_REASONS, PublicReason, REPORTER_NATURE_DETAIL_OPTIONS, REPORTER_NATURE_OPTIONS, RIGHTS_TYPES,
} from '../../../core/occurrence/public-reasons';

/**
 * Portal público do site: registo de ocorrências sem login (Etapa 6).
 *
 * A escolha é feita em linguagem comum ("o que quer comunicar?") e traduzida para a cascata
 * âmbito/subâmbito/tipo em `public-reasons.ts` — antes pedia-se ao cidadão que soubesse escolher
 * entre RGPD, RGPC e Sistemas de Gestão antes de conseguir escrever uma linha.
 */
@Component({
  selector: 'app-occurrence-public-form',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './occurrence-public-form.html',
})
export class OccurrencePublicForm {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly tenant = this.route.snapshot.paramMap.get('tenant') ?? '';

  protected readonly reasons = PUBLIC_REASONS.filter((reason) => !reason.hiddenFromHome);
  protected readonly rightsOptions = RIGHTS_TYPES.map((value) => ({ value, label: TYPE_LABELS[value] }));
  /** Os motivos são os do subâmbito do caminho escolhido, não a lista toda. */
  protected readonly reasonOptions = computed(() => complaintReasonOptions(this.reason()?.subScope ?? null));
  /** As categorias são as do âmbito do motivo escolhido, não uma lista só. */
  protected readonly categoryOptions = computed(() => reportCategoryOptions(this.reason()?.scope ?? null));
  protected readonly categories = signal<ReportCategory[]>([]);
  protected readonly incidents = signal<IncidentNature[]>([]);
  protected readonly healthDataNote = HEALTH_DATA_NOTE;

  protected readonly loading = signal(false);
  protected readonly serverError = signal<string | null>(null);
  protected readonly trackingCode = signal<string | null>(null);
  protected readonly copied = signal(false);

  protected readonly reason = signal<PublicReason | null>(null);
  /** Chegou pelo canal de denúncias: o "Mudar" regressa lá. */
  protected readonly fromChannel = signal(false);

  protected readonly form = this.fb.nonNullable.group({
    right: ['ACESSO' as OccurrenceType],
    complaintReason: [null as ComplaintReason | null],
    complaintReasonOther: [''],
    reportCategoryOther: [''],
    reporterNature: [null as ReporterNature | null],
    reporterNatureDetail: [null as ReporterNatureDetail | null],
    title: ['', Validators.required],
    message: ['', Validators.required],
    identified: [false],
    participantName: [''],
    participantEmail: [''],
    participantPhone: [''],
    participantConfidential: [false],
    participantRelation: [null as OrganizationRelation | null],
    participantWorkerType: [null as WorkerType | null],
  });

  protected readonly relationOptions = ORGANIZATION_RELATION_OPTIONS;
  protected readonly workerTypeOptions = WORKER_TYPE_OPTIONS;
  /** O tipo de colaborador só faz sentido depois de a relação ser "colaborador". */
  protected readonly participantRelation = signal<OrganizationRelation | null>(null);
  protected readonly showWorkerType = computed(() => this.participantRelation() === 'COLABORADOR');

  protected onRelationChange(): void {
    const relation = this.form.controls.participantRelation.value;
    this.participantRelation.set(relation);
    if (relation !== 'COLABORADOR') {
      this.form.controls.participantWorkerType.setValue(null);
    }
  }

  protected readonly natureOptions = REPORTER_NATURE_OPTIONS;
  protected readonly natureDetailOptions = REPORTER_NATURE_DETAIL_OPTIONS;
  protected readonly reporterNature = signal<ReporterNature | null>(null);
  /** Opções de seleção múltipla da natureza escolhida (null: a natureza não tem). */
  protected readonly natureChoices = computed(() => {
    const nature = this.reporterNature();
    return nature ? NATURE_CHOICES[nature] ?? null : null;
  });
  /** Rótulo do campo do relato: o elogio e o pedido de direitos não relatam "o que aconteceu". */
  protected readonly messageLabel = computed(() => {
    const reason = this.reason();
    if (reason?.nature === 'ELOGIO_RECONHECIMENTO') return 'Deixe aqui o seu elogio/reconhecimento';
    if (reason?.rightsChoice) return 'Descreva o seu pedido';
    return 'O que aconteceu?';
  });
  protected readonly reporterNatureDetail = signal<string | null>(null);
  /**
   * Na denúncia, o assunto e o relato só aparecem depois de classificada: com pelo menos uma
   * opção marcada, ou com a sub-opção escolhida em "Outras infrações" (que não tem opções).
   */
  protected readonly showReportText = computed(() => {
    if (this.reason()?.natureChoice !== true) return true;
    const nature = this.reporterNature();
    if (!nature) return false;
    if (nature === 'OUTRAS_INFRACOES') return this.reporterNatureDetail() !== null;
    if (!this.natureChoices()) return true;
    return this.categories().length + this.incidents().length > 0;
  });
  protected readonly showOtherText = computed(() => this.categories().includes('OUTRA_IRREGULARIDADE'));
  /** Dados pessoais e "Outra irregularidade": o servidor só aceita a denúncia identificada. */
  protected readonly natureLocksIdentity = computed(() => this.reason()?.natureChoice === true
    && (this.reporterNature() === 'PROTECAO_DADOS' || this.categories().includes('OUTRA_IRREGULARIDADE')));

  protected onNatureChange(): void {
    const nature = this.form.controls.reporterNature.value;
    this.reporterNature.set(nature);
    if (nature !== 'OUTRAS_INFRACOES') this.form.controls.reporterNatureDetail.setValue(null);
    this.reporterNatureDetail.set(this.form.controls.reporterNatureDetail.value);
    // Cada natureza tem as suas opções: trocar de natureza limpa as escolhas.
    this.categories.set([]);
    this.incidents.set([]);
    this.categoriesError.set(null);
    this.form.controls.reportCategoryOther.setValue('');
    this.form.controls.reportCategoryOther.updateValueAndValidity();
    this.syncIdentityLock();
  }

  private syncIdentityLock(): void {
    const identified = this.form.controls.identified;
    if (this.natureLocksIdentity()) {
      identified.setValue(true);
      identified.disable();
    } else if (identified.disabled) {
      identified.enable();
    }
  }

  protected readonly complaintReason = signal<ComplaintReason | null>(null);
  protected readonly categoriesError = signal<string | null>(null);

  /**
   * Identificação obrigatória: exercício de direitos (o backend recusa anónimo) e reclamações
   * sobre produto/serviço, atendimento ou outro motivo, que não admitem anonimato pelo site.
   */
  protected readonly mustIdentify = computed(() => {
    const reason = this.reason();
    if (reason?.requiresIdentification) return true;
    // Fora das categorias previstas não há como tratar um caso anónimo.
    if (!reason?.natureChoice && this.categories().includes('OUTRA_IRREGULARIDADE')) return true;
    const complaint = this.complaintReason();
    return complaint === 'PRODUTO_SERVICO'
      || complaint === 'ATENDIMENTO_COMUNICACAO'
      || complaint === 'OUTRO';
  });

  /**
   * Aviso sobre dados de saúde. Aparece antes de se escrever a mensagem, nas comunicações em
   * que quem participa tende a juntar informação clínica que não é precisa para tratar o caso.
   */
  protected readonly showHealthDataNote = computed(() => {
    const fixed = this.reason()?.reportCategories ?? [];
    const chosen = this.categories();
    return [...fixed, ...chosen].some((category) => HEALTH_DATA_CATEGORIES.includes(category))
      || this.complaintReason() === HEALTH_DATA_COMPLAINT_REASON;
  });

  constructor() {
    // O motivo escolhido vive no endereço (?motivo=): refresh e Voltar ficam no portal, e o
    // Voltar do passo 2 regressa à escolha em vez de sair daqui.
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.fromChannel.set(params.get('origem') === 'canal');
      const id = params.get('motivo');
      const reason = id ? PUBLIC_REASONS.find((item) => item.id === id) ?? null : null;
      if (reason?.id === this.reason()?.id) return;
      if (reason) this.applyReason(reason); else this.reason.set(null);
    });
  }

  protected selectReason(reason: PublicReason): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { motivo: reason.id }, queryParamsHandling: 'merge' });
  }

  private applyReason(reason: PublicReason): void {
    this.reason.set(reason);
    this.serverError.set(null);
    this.categories.set([]);
    this.incidents.set([]);
    this.categoriesError.set(null);
    this.form.controls.reportCategoryOther.setValue('');
    this.reporterNature.set(null);
    this.reporterNatureDetail.set(null);
    this.form.controls.reporterNature.setValue(null);
    this.form.controls.reporterNatureDetail.setValue(null);
    this.form.controls.identified.enable();
    this.complaintReason.set(reason.complaintReason ?? null);
    this.form.controls.complaintReason.setValue(reason.complaintReason ?? null);
    if (reason.requiresIdentification) {
      this.form.controls.identified.setValue(true);
    }
  }

  protected onComplaintReasonChange(): void {
    this.complaintReason.set(this.form.controls.complaintReason.value);
    if (this.mustIdentify()) this.form.controls.identified.setValue(true);
  }

  protected toggleCategory(category: ReportCategory, checked: boolean): void {
    // Sem o filtro, dois eventos a dizer "marcado" gravavam a categoria duas vezes — e ela
    // aparecia repetida na ocorrência.
    const restantes = this.categories().filter((item) => item !== category);
    this.categories.set(checked ? [...restantes, category] : restantes);
    this.categoriesError.set(null);
    if (!this.categories().includes('OUTRA_IRREGULARIDADE')) {
      this.form.controls.reportCategoryOther.setValue('');
      this.form.controls.reportCategoryOther.updateValueAndValidity();
    }
    // "Outra irregularidade" não admite anonimato: a escolha prende a identificação.
    if (this.mustIdentify()) this.form.controls.identified.setValue(true);
    this.syncIdentityLock();
  }

  protected toggleIncident(incident: IncidentNature, checked: boolean): void {
    const restantes = this.incidents().filter((item) => item !== incident);
    this.incidents.set(checked ? [...restantes, incident] : restantes);
    this.categoriesError.set(null);
  }

  protected isChoiceSelected(value: string): boolean {
    return this.natureChoices()?.field === 'incidentNatures'
      ? this.incidents().includes(value as IncidentNature)
      : this.categories().includes(value as ReportCategory);
  }

  protected toggleChoice(value: string, checked: boolean): void {
    if (this.natureChoices()?.field === 'incidentNatures') this.toggleIncident(value as IncidentNature, checked);
    else this.toggleCategory(value as ReportCategory, checked);
  }

  protected isCategorySelected(category: ReportCategory): boolean {
    return this.categories().includes(category);
  }

  protected clearReason(): void {
    if (this.fromChannel()) {
      this.router.navigate(['/', this.tenant, 'canal-denuncias']);
      return;
    }
    this.router.navigate([], { relativeTo: this.route, queryParams: { motivo: null }, queryParamsHandling: 'merge' });
  }

  protected submit(): void {
    const reason = this.reason();
    // Um segundo clique com o envio a caminho registava a participação duas vezes.
    if (!reason || this.loading()) return;
    const identified = this.form.controls.identified.value || this.mustIdentify() || this.natureLocksIdentity();
    // A folha de campos de 2026-10-01 pede nome e e-mail — só o telefone é opcional. Dizer
    // "a identificação é obrigatória" e aceitar uma participação sem nome era meia identificação.
    if (identified && !this.form.controls.participantName.value.trim()) {
      this.form.controls.participantName.setErrors({ required: true });
    }
    if (identified && !this.form.controls.participantEmail.value.trim()) {
      this.form.controls.participantEmail.setErrors({ required: true });
    }
    if (reason.complaintChoice && this.form.controls.complaintReason.value === null) {
      this.form.controls.complaintReason.setErrors({ required: true });
    }
    if (this.complaintReason() === 'OUTRO' && !this.form.controls.complaintReasonOther.value?.trim()) {
      this.form.controls.complaintReasonOther.setErrors({ required: true });
    }
    if (reason.natureChoice && !this.form.controls.reporterNature.value) {
      this.form.controls.reporterNature.setErrors({ required: true });
    }
    if (reason.natureChoice && this.reporterNature() === 'OUTRAS_INFRACOES'
      && !this.form.controls.reporterNatureDetail.value) {
      this.form.controls.reporterNatureDetail.setErrors({ required: true });
    }
    if (reason.categoryChoice && !this.categories().length) {
      this.categoriesError.set('Escolha pelo menos uma categoria.');
    }
    const choices = reason.natureChoice ? this.natureChoices() : null;
    if (choices && !(choices.field === 'incidentNatures' ? this.incidents() : this.categories()).length) {
      this.categoriesError.set('Escolha pelo menos uma opção.');
    }
    if (this.showOtherText() && !this.form.controls.reportCategoryOther.value.trim()) {
      this.form.controls.reportCategoryOther.setErrors({ required: true });
    }
    if (this.form.invalid || this.categoriesError()) {
      this.form.markAllAsTouched();
      this.focusFirstInvalid();
      return;
    }
    this.loading.set(true);
    this.serverError.set(null);
    const value = this.form.getRawValue();
    const body = reason.natureChoice ? {
      reporterNature: value.reporterNature,
      reporterNatureDetail: value.reporterNature === 'OUTRAS_INFRACOES' ? value.reporterNatureDetail : null,
      reportCategories: this.natureChoices()?.field === 'reportCategories' ? this.categories() : [],
      incidentNatures: this.natureChoices()?.field === 'incidentNatures' ? this.incidents() : [],
      reportCategoryOther: this.showOtherText() ? value.reportCategoryOther.trim() : null,
      title: value.title,
      message: value.message,
      ...this.participantFields(identified),
    } : {
      scope: reason.scope,
      scopeOther: reason.scopeOther ?? null,
      subScope: reason.subScope ?? null,
      subScopeOther: null,
      integratedSystems: [],
      type: reason.rightsChoice ? value.right : reason.type,
      typeOther: null,
      nature: reason.nature ?? null,
      complaintReason: reason.nature === 'RECLAMACAO' ? value.complaintReason : null,
      complaintReasonOther: this.complaintReason() === 'OUTRO'
        ? value.complaintReasonOther?.trim() || null
        : null,
      reportCategories: reason.nature === 'DENUNCIA'
        ? (reason.reportCategories ?? this.categories())
        : [],
      reportCategoryOther: this.showOtherText() ? value.reportCategoryOther.trim() : null,
      title: value.title,
      message: value.message,
      ...this.participantFields(identified),
    };
    this.http.post<{ trackingCode: string }>(`/api/t/${this.tenant}/publico/ocorrencias`, body).subscribe({
      next: (response) => this.trackingCode.set(response.trackingCode),
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        this.serverError.set((error.error as ApiError)?.message ?? 'Não foi possível enviar a participação.');
      },
    });
  }

  private participantFields(identified: boolean) {
    const value = this.form.getRawValue();
    return {
      participantIdentification: identified ? 'IDENTIFICADO' : 'ANONIMO',
      participantName: identified ? value.participantName.trim() || null : null,
      participantEmail: identified ? value.participantEmail.trim() : null,
      participantPhone: identified ? value.participantPhone.trim() || null : null,
      participantConfidential: identified ? value.participantConfidential : null,
      participantRelation: identified ? value.participantRelation : null,
      participantWorkerType: identified && this.showWorkerType() ? value.participantWorkerType : null,
    };
  }

  /** Leva a vista e o foco ao primeiro campo por corrigir, na ordem em que aparecem no ecrã. */
  private focusFirstInvalid(): void {
    setTimeout(() => {
      const target = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(
        '[formcontrolname], input[type=checkbox]:not([formcontrolname])',
      )).find((element) => element.hasAttribute('formcontrolname')
        ? element.classList.contains('ng-invalid')
        : !!this.categoriesError());
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
    });
  }

  protected copyCode(): void {
    const code = this.trackingCode();
    if (!code) return;
    navigator.clipboard?.writeText(code).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }
}
